import { FC, useEffect } from 'react';
import { useAppSelector } from 'helpers/hooks';
import { getUserData } from 'selectors/user';
import { IEvent } from 'store/eventPage/types';
import { IPage } from 'store/donatePage/types';
import classNames from 'classnames';
import { ProfileCard } from 'components/Cards/ProfileCard';
import { Image } from 'components/Image';
import { capitalizeString, formatNumberAsCurrency, getUrlParams, scrollToTop } from 'helpers/utils';
import { PopupModal } from 'components/PopupModal';
import { CTA } from 'components/ButtonBrand';
import { useShareUrl } from 'hooks/useShareUrl';
import { trackPixel, trackPurchaseOnce } from 'helpers/pixel';
import { PaymentMethodType } from 'store/pay/types';
import { getPaymentMethodTypeDisplay } from 'screens/Checkout/Form/utils';
import { trackCheckoutConfirmationViewed } from 'helpers/mixpanelEvents';
import { useCompleteSavedCart } from 'hooks/useCompleteSavedCart';
import { useParams } from 'react-router-dom';
import { getActionVerb, getOfflineInstructionsByPledgeMethod } from 'helpers/donate';

interface Props {
  conversionId?: string;
  date: string;
  donationAmount: number;
  headerBackground?: string;
  isEvent: boolean;
  page?: IPage | IEvent;
  pageImage: string;
  recipientNames: string[];
  textColor: string;
  time: string;
}

export const PledgeConfirmation: FC<Props> = ({
  conversionId,
  donationAmount,
  page,
  date,
  headerBackground,
  isEvent,
  pageImage,
  recipientNames,
  textColor,
  time,
}) => {
  const { urlPartner } = getUrlParams();
  const { pledgeMethod } = useParams<{ pledgeMethod: PaymentMethodType | undefined }>();
  const { user } = useAppSelector(state => ({
    user: getUserData(state),
  }));
  const { handleShare, isPopupOpen } = useShareUrl({ userRefCode: user?.ref_code });
  const pledgeMethodDisplay = getPaymentMethodTypeDisplay(pledgeMethod);
  const cartId = useCompleteSavedCart(true, { page, paymentProcessor: 'Offline' });

  useEffect(() => {
    scrollToTop();
    trackPurchaseOnce(conversionId, { amount: donationAmount, unit: 'cents' });
    trackPixel('universalPixel');
    trackCheckoutConfirmationViewed({
      payment_method: pledgeMethod,
      is_recurring: false,
      amount: donationAmount,
      client_id: page?.client_id,
      is_stripe: true,
      // Stripe flow, offline payment — see `PaymentProcessor`. Not a contradiction.
      payment_processor: 'Offline',
      ...(cartId && { cart_id: cartId }),
    });
    // Fire once per view; re-running on prop changes would double-count.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const donateText = getActionVerb(page, urlPartner === 'f4d-nvi' ? 'contribute' : undefined);
  const offlineInstructions =
    page && pledgeMethod ? getOfflineInstructionsByPledgeMethod(page, pledgeMethod) : [];

  return (
    <div className="confirmation">
      <div className={classNames('confirmation-background-container', { 'bg-ink-blue': isEvent })}>
        <div className="confirmation-container">
          <div className="confirmation-container-left">
            <span className={`eyebrow-text mb5 color-${textColor}`}>
              {`${pledgeMethodDisplay} Pledge${isEvent ? ' / Ticket Purchase' : ''}  Confirmation`}
            </span>
            <span className={`h4-text mb5 color-${textColor}`}>
              Thank you for your {pledgeMethodDisplay} pledge!
            </span>
            <span className={`body-text color-${textColor}`}>
              {(page as IEvent)?.post_donate_msg ||
                `Keep the momentum going and invite your friends and family to ${donateText}.`}
            </span>
            {pageImage && (
              <Image className="confirmation-image" alt="Donate page logo" src={pageImage} />
            )}
            <div className="confirmation-button-row">
              <CTA className="button button-brand" onClick={handleShare}>
                Share link
              </CTA>
            </div>
          </div>
          <div className="confirmation-container-right">
            <div className="confirmation-card-container mb14">
              <div className="text-left mb14">
                <span className="body-text">
                  We’ve emailed you a receipt for your records.
                  <br />
                  Please contact us at team@oath.vote if you have any questions.
                </span>
              </div>
              <ProfileCard
                title={`${capitalizeString(pledgeMethodDisplay)} Instructions`}
                align="center"
                headerBackground={headerBackground}
                smallTitle={true}
              >
                <div className="confirmation-card-content">
                  <div className="body-text mb4">Contribution Summary</div>
                  <div className="offline-instructions-row mb13">
                    <div className="offline-instructions-label display-grid g3">
                      <span className="h4-text mb2">
                        {formatNumberAsCurrency(donationAmount / 100)}
                      </span>
                      <span className="utility-text">{`to ${recipientNames.join(', ')}`}</span>
                    </div>
                    <div className="offline-instructions-value display-grid">
                      <span className="h4-text mb2">{date}</span>
                      <span className="utility-text">{`at ${time
                        .toLowerCase()
                        .replace(/ /g, '')}`}</span>
                    </div>
                  </div>
                  <div className="offline-instructions-container">
                    {offlineInstructions?.length > 0 &&
                      offlineInstructions.map(instructionObj => (
                        <div key={instructionObj.label} className="offline-instructions-row">
                          <div className="offline-instructions-label">
                            <span className="body-small-text color-dark-gray">
                              {instructionObj.label}:
                            </span>
                          </div>
                          <div className="offline-instructions-value">
                            <span className="body-small-text color-ink-blue">
                              {instructionObj.value}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </ProfileCard>
            </div>
          </div>
          <PopupModal autoClose={true} isSelected={isPopupOpen} title="Copied to Clipboard" />
        </div>
      </div>
    </div>
  );
};
